import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SystemStatus from "@/components/SystemStatus";
import LoadingScreen from "@/components/LoadingScreen";
import CursorReticle from "@/components/CursorReticle";
import HomePage from "@/pages/HomePage";
import NetworkPage from "@/pages/NetworkPage";
import AgentsPage from "@/pages/AgentsPage";
import MachinesPage from "@/pages/MachinesPage";
import TasksPage from "@/pages/TasksPage";
import EconomyPage from "@/pages/EconomyPage";
import TerminalPage from "@/pages/TerminalPage";
import ThesisPage from "@/pages/ThesisPage";
import DocsPage from "@/pages/DocsPage";
import AboutPage from "@/pages/AboutPage";
import LaunchPage from "@/pages/LaunchPage";
import NotFoundPage from "@/pages/NotFoundPage";

const Page = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.22, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const RouteFlash = () => {
  const location = useLocation();
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 380);
    return () => clearTimeout(t);
  }, [location.pathname]);
  return (
    <AnimatePresence>
      {flash && (
        <motion.div
          data-testid="page-transition-overlay"
          className="fixed inset-0 z-[80] pointer-events-none bg-arc-black/80 backdrop-blur-sm flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div className="font-mono text-[11px] tracking-[0.3em] text-arc-cyan">
            <div>ARCMECH SYSTEM</div>
            <div className="text-arc-silver/60 mt-1">&gt; SWITCHING MODULE<span className="anim-blink">_</span></div>
            <div className="mt-3 h-px w-48 bg-arc-metal overflow-hidden">
              <motion.div className="h-full bg-arc-electric" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 0.3 }} />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Shell = () => (
  <>
    <ScrollToTop />
    <RouteFlash />
    <Navbar />
    <main id="main-content">
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<Page><HomePage /></Page>} />
          <Route path="/network" element={<Page><NetworkPage /></Page>} />
          <Route path="/agents" element={<Page><AgentsPage /></Page>} />
          <Route path="/machines" element={<Page><MachinesPage /></Page>} />
          <Route path="/tasks" element={<Page><TasksPage /></Page>} />
          <Route path="/economy" element={<Page><EconomyPage /></Page>} />
          <Route path="/terminal" element={<Page><TerminalPage /></Page>} />
          <Route path="/thesis" element={<Page><ThesisPage /></Page>} />
          <Route path="/docs" element={<Page><DocsPage /></Page>} />
          <Route path="/about" element={<Page><AboutPage /></Page>} />
          <Route path="/launch" element={<Page><LaunchPage /></Page>} />
          <Route path="*" element={<Page><NotFoundPage /></Page>} />
        </Routes>
      </AnimatePresence>
    </main>
    <Footer />
    <SystemStatus />
    <CursorReticle />
  </>
);

function App() {
  const [booted, setBooted] = useState(() => sessionStorage.getItem("arcmech.boot.v1") === "1");
  const finishBoot = () => {
    sessionStorage.setItem("arcmech.boot.v1", "1");
    setBooted(true);
  };
  return (
    <div className="min-h-screen bg-arc-black">
      <BrowserRouter>
        {!booted && <LoadingScreen onDone={finishBoot} />}
        <Shell />
      </BrowserRouter>
    </div>
  );
}

export default App;
